<script setup lang="ts">
import { computed } from 'vue'
import { 
  AlertCircle, 
  CheckCircle2, 
  AlertTriangle, 
  Info, 
  X 
} from '@lucide/vue'

interface Props {
  type?: 'error' | 'success' | 'warning' | 'info'
  title?: string
  message?: string
  dismissible?: boolean
  outline?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'error',
  title: '',
  message: '',
  dismissible: false,
  outline: false
})

const emit = defineEmits<{
  (e: 'dismiss'): void
}>()

const icons = {
  error: AlertCircle,
  success: CheckCircle2,
  warning: AlertTriangle,
  info: Info
}

const styles = {
  error: {
    bg: 'bg-error-bg',
    border: 'border-error/20',
    text: 'text-error-text',
    icon: 'text-error',
    accent: 'bg-error',
    outline: 'border-error text-error-text bg-transparent'
  },
  success: {
    bg: 'bg-success-bg',
    border: 'border-success/20',
    text: 'text-success-text',
    icon: 'text-success',
    accent: 'bg-success',
    outline: 'border-success text-success-text bg-transparent'
  },
  warning: {
    bg: 'bg-warn-bg',
    border: 'border-warn/20',
    text: 'text-warn-text',
    icon: 'text-warn',
    accent: 'bg-warn',
    outline: 'border-warn text-warn-text bg-transparent'
  },
  info: {
    bg: 'bg-info-bg',
    border: 'border-info/20',
    text: 'text-info-text',
    icon: 'text-info',
    accent: 'bg-info',
    outline: 'border-info text-info-text bg-transparent'
  }
}

const currentStyle = computed(() => styles[props.type])
</script>

<template>
  <div 
        class="relative overflow-hidden rounded-card border p-4 transition-[background-color,border-color,color] duration-150"
    :class="[
      outline ? currentStyle.outline : `${currentStyle.bg} ${currentStyle.border} ${currentStyle.text}`,
      type === 'error' ? 'motion-safe:animate-[shake_0.5s_ease-in-out]' : ''
    ]"
    role="alert"
    :data-testid="type === 'error' ? 'error-message' : undefined"
  >
    <!-- Left Accent Bar -->
    <div 
      v-if="!outline"
      class="absolute left-0 top-0 bottom-0 w-1" 
      :class="currentStyle.accent"
    />

    <div class="flex gap-3">
      <!-- Icon -->
      <div class="shrink-0 mt-1">
        <component
          :is="icons[type]"
          class="size-5"
          :class="currentStyle.icon"
        />
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <h5
          v-if="title"
          class="text-body-sm font-bold uppercase tracking-wide leading-tight mb-1"
        >
          {{ title }}
        </h5>
        <p class="text-xs font-medium leading-relaxed opacity-90">
          <slot>{{ message }}</slot>
        </p>
      </div>

      <!-- Close Button -->
      <button
        v-if="dismissible"
        aria-label="Tutup Peringatan"
        class="shrink-0 -mr-1 -mt-1 p-1 rounded-lg hover:bg-primary/5 transition-colors"
        @click="emit('dismiss')"
      >
        <X class="size-4 opacity-50" />
      </button>
    </div>
  </div>
</template>
